<template>
  <div class="py-2">
    <b-overlay :show="isLoading">
      <vams-collapse title="DVIR Defects" :visible="visible">
        <b-table
          :items="openUnitDvirDefects"
          :fields="fields"
          table-variant="blue"
          thead-tr-class="text-small"
          tbody-tr-class="bg-half-alpha"
        >
          <template #cell(selected)="{ item }">
            <div class="text-center">
              <input
                v-show="!item.jobId && !item.serviceOrderId"
                v-model="selectedDVIRs"
                type="checkbox"
                :value="item"
                :disabled="!item.jobId"
              />
              <img
                v-show="item.jobId && !item.serviceOrderId"
                src="/images/square-check-solid.svg"
                width="32"
                height="auto"
                alt="Previous Page"
              />
              <font-awesome-icon v-show="item.serviceOrderId" icon="check" class="text-success" />
            </div>
          </template>

          <template #head(remark)>
            <div>
              Defect
              <sup>
                <font-awesome-icon
                  id="defect_tooltip"
                  icon="info-circle"
                  size="lg"
                  class="mx-1 text-primary"
                  display="inline-block"
                />
              </sup>
              <b-tooltip target="defect_tooltip" triggers="hover">
                Defects must be marked as Repaired/Resolved in Telematics portal in order to show as Resolved in VAMS.
                Changes may take up to an hour to show in VAMS
              </b-tooltip>
            </div>
          </template>

          <template #cell(remark)="{ item }">
            <div class="text-left d-flex">
              <div>
                {{ item.name }} - {{ item.remark }}

                <b-badge :variant="saftyBadgeVariant(item.isSafe)" class="ml-4" style="min-width: 50px">
                  {{ safetyText(item.isSafe) }}
                </b-badge>
              </div>
            </div>
          </template>

          <template #cell(job)="{ item }">
            <div v-if="item.serviceOrderId" class="ml-2">
              <span v-if="item.serviceOrderId != serviceOrderId">
                <b-link :to="'/units/' + $route.params.unitId + '/service-orders/' + item.serviceOrderId">
                  {{ item.serviceOrderId }}
                </b-link>
              </span>
              OpsId {{ item.operationId }}
            </div>
            <v-select
              v-else
              v-model="item.job"
              label="description"
              :placeholder="item.isDuplicate ? 'Duplicate Defect' : 'Select Repair Operation'"
              :options="filteredJobs"
              :clearable="true"
              :filterable="true"
              :filter-by="operationDropdownSearchFilter"
              :disabled="item.isDuplicate"
              @input="selectRow(item)"
            >
              <template #selected-option="operation">
                <span class="text-uppercase">{{ operation.id }} - {{ operation.description }}</span>
              </template>
              <template #option="operation">
                <span class="text-uppercase">{{ operation.id }} - {{ operation.description }}</span>
              </template>
            </v-select>
          </template>

          <template #cell(isDuplicate)="{ item }">
            <div class="text-center">
              <b-form-checkbox
                v-model="item.isDuplicate"
                switch
                :disabled="item.jobId != null || item.serviceOrderId != null"
                @input="setDuplicate(item)"
              />
            </div>
          </template>
          <template #cell(reportedBy)="{ item }">
            <div class="text-left d-flex flex-column">
              {{ item.reportedBy ? item.reportedBy.name : '' }}
            </div>
          </template>

          <template #cell(dateReported)="{ item }">
            <div class="text-left d-flex flex-column">
              {{ item.dateReported | date }}
            </div>
          </template>
        </b-table>
      </vams-collapse>
    </b-overlay>
  </div>
</template>

<script>
// components
import CollapseComponent from '@/shared/components/ui/CollapseComponent';
import vSelect from 'vue-select';
import Cloner from '@/shared/helpers/cloner';
// vuex
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { ServiceOrderGetters } from '@/shared/store/service-order/types';
import { UnitGetters } from '@/shared/store/unit/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import { ServiceOrderMutations } from '@/shared/store/service-order/types';
import { UnitMutations } from '@/shared/store/unit/types';

export default {
  name: 'ServiceOrderDVIRs',
  components: {
    vSelect,
    'vams-collapse': CollapseComponent
  },
  props: {
    visible: Boolean()
  },
  data: function () {
    return {
      fields: [
        { key: 'selected', label: '', sortable: false, tdClass: 'align-middle', thStyle: 'width: 50px' },
        { key: 'remark', label: 'Defect', sortable: true, tdClass: 'align-middle' },
        { key: 'job', label: 'Job ID', sortable: true, tdClass: 'align-middle', thStyle: 'width: 475px' },
        { key: 'isDuplicate', label: 'Duplicate', sortable: false },
        { key: 'reportedBy', label: 'Reported By', sortable: true, tdClass: 'align-middle' },
        { key: 'dateReported', label: 'Date Reported', sortable: true, tdClass: 'align-middle' }
      ],
      isLoading: false,
      clonedUnitDvirDefects: [],
      dvirDefectJobs: []
    };
  },
  computed: {
    ...mapGetters({
      unitDvirDefects: UnitGetters.GET_UNIT_DVIR_DEFECTS,
      unit: UnitGetters.GET_UNIT,
      serviceOrderId: ServiceOrderGetters.GET_SERVICE_ORDER_ID,
      nonScheduledOperationsList: LookupGetters.GET_NON_SCHEDULED_OPERATIONS_LIST
    }),
    openUnitDvirDefects() {
      return this.clonedUnitDvirDefects?.filter(d => d.dateResolved == null);
    },
    filteredJobs() {
      return this.nonScheduledOperationsList.filter(j => j.id.indexOf('&') == -1);
    },
    selectedDVIRs: {
      get() {
        return this.dvirDefectJobs;
      },
      set(value) {
        this.dvirDefectJobs.push(value);
      }
    }
  },
  watch: {
    dvirDefectJobs: {
      handler() {
        let jobs = Cloner.deepClone(this.dvirDefectJobs);
        this[ServiceOrderMutations.SET_SELECTED_DVIR_DEFECTS_JOBS](jobs);
      }
    }
  },
  async created() {
    await this.cloneDvirDefects();
  },
  methods: {
    ...mapActions([]),
    ...mapMutations([ServiceOrderMutations.SET_SELECTED_DVIR_DEFECTS_JOBS, UnitMutations.SET_DVIR_DEFECT_IS_DUPLICATE]),
    async cloneDvirDefects() {
      //This is to avoid the error of mutating the list of dvirDefects with the jobId outside of the mutations
      this.clonedUnitDvirDefects = Cloner.deepClone(this.unitDvirDefects);
    },
    selectRow(item) {
      item.jobId = item.job?.id;
      item.jobName = item.job?.description;
      let existingJob = this.dvirDefectJobs.find(j => j.dvirDefectId == item.dvirDefectId);
      if (existingJob) {
        if (!item.jobId) {
          this.dvirDefectJobs.pop(item);
        } else {
          existingJob.jobId = item.jobId;
        }
      } else {
        this.dvirDefectJobs.push(item);
      }
    },
    setDuplicate(item) {
      this[UnitMutations.SET_DVIR_DEFECT_IS_DUPLICATE](item);
    },
    saftyBadgeVariant(isSafe) {
      if (isSafe === null) return 'secondary';
      return isSafe ? 'success' : 'danger';
    },
    safetyText(isSafe) {
      if (isSafe == null) return 'Unknown';
      return isSafe ? 'Safe' : 'Unsafe';
    },
    operationDropdownSearchFilter(option, label, search) {
      return this.dropdownSearchFilter(option.id, option.description, search);
    },
    dropdownSearchFilter(id, description, search) {
      search = search.toLowerCase();
      id = id.toLowerCase();
      description = description.toLowerCase();

      return id.indexOf(search) > -1 || description.indexOf(search) > -1;
    }
  }
};
</script>
